<template>
  <layout title="Player de Entregas" :options="options" :rotas="rotas">
    <section class="delivery-player" :class="{ 'delivery-player-dark': $theme.dark }" :style="playerViewportStyle">
      <div class="player-map">
        <div ref="playerDeliveryMap" class="player-leaflet-map"></div>
        <div v-if="!currentDelivery" class="player-map-empty">
          <v-icon size="54" color="#fbbf24">mdi-map-marker-path</v-icon>
          <strong>Sem rotas no momento</strong>
          <span>{{ locationLabel }}</span>
        </div>
        <a v-if="mapGoogleUrl" class="google-route-button" :href="mapGoogleUrl" target="_blank">
          <v-icon small color="white">mdi-google-maps</v-icon>
          Abrir no Google Maps
        </a>
      </div>

      <aside class="player-panel">
        <div class="current-card transition-card" v-if="transitionDelivery">
          <div class="transition-icon" :class="transitionStatus">
            <v-icon color="white" size="34">{{ getIcon(transitionStatus) }}</v-icon>
          </div>
          <span class="transition-eyebrow">{{ getStatus(transitionStatus) }}</span>
          <strong>{{ transitionTitle }}</strong>
          <small>{{ clientName(transitionDelivery) }} - Venda #{{ saleNumber(transitionDelivery) }}</small>
          <p>{{ transitionMessage }}</p>
          <div class="transition-actions">
            <v-btn color="green" dark depressed :disabled="nextDeliveryDisabled" @click="goToNextDelivery">
              <v-icon left small>mdi-skip-next</v-icon>Ir para a próxima
            </v-btn>
            <v-btn outlined :dark="$theme.dark" @click="$router.push('/entregas')">
              <v-icon left small>mdi-format-list-bulleted</v-icon>Ver lista de entrega
            </v-btn>
          </div>
        </div>

        <div class="current-card" v-else-if="currentDelivery">
          <v-btn
            class="delivery-photo-button"
            icon
            small
            :loading="photoUploading"
            title="Registrar foto"
            @click="$refs.photoInput.click()"
          >
            <v-icon small>mdi-camera-outline</v-icon>
          </v-btn>
          <input ref="photoInput" class="d-none" type="file" accept="image/*" capture="environment" @change="uploadPhoto" />

          <div class="current-status">
            <v-icon :color="getColor(currentDelivery.status)">{{ getIcon(currentDelivery.status) }}</v-icon>
            <span>{{ getStatus(currentDelivery.status) }}</span>
          </div>
          <strong>{{ clientName(currentDelivery) }}</strong>
          <small>Venda #{{ saleNumber(currentDelivery) }}</small>
          <p><v-icon size="18">mdi-map-marker-outline</v-icon>{{ addressLabel(currentDelivery.address_destiny) }}</p>
          <p><v-icon size="18">mdi-map-marker-distance</v-icon>{{ distanceLabel(currentDelivery) }}</p>
          <p><v-icon size="18">mdi-clock-fast</v-icon>{{ routeDurationLabel || "Tempo indisponível" }}</p>

          <div v-if="!pendingOutcomeStatus" class="player-actions">
            <v-btn color="green" dark depressed :loading="isActionLoading('done')" @click="openOutcome('done')">
              <v-icon left small>mdi-check-circle-outline</v-icon>Entrega concluída
            </v-btn>
            <v-btn color="red" dark depressed :loading="isActionLoading('canceled')" @click="openOutcome('canceled')">
              <v-icon left small>mdi-cancel</v-icon>Não consegui entregar
            </v-btn>
          </div>

          <div v-else class="outcome-form">
            <div class="outcome-title">
              <v-icon :color="getColor(pendingOutcomeStatus)">{{ getIcon(pendingOutcomeStatus) }}</v-icon>
              <strong>{{ pendingOutcomeTitle }}</strong>
            </div>
            <v-textarea
              class="outcome-comment-field"
              dense
              filled
              :dark="$theme.dark"
              rows="3"
              label="Comentário ou observação"
              v-model="pendingOutcomeComment"
            />
            <div v-if="pendingOutcomePhotoPreview" class="outcome-photo-line">
              <img :src="pendingOutcomePhotoPreview" alt="Foto da entrega" />
              <span>{{ pendingOutcomeFileName }}</span>
            </div>
            <div class="outcome-actions">
              <v-btn text :dark="$theme.dark" :disabled="isSubmittingOutcome" @click="cancelOutcome">
                Voltar
              </v-btn>
              <v-btn :color="pendingOutcomeColor" dark depressed :loading="isSubmittingOutcome" :disabled="photoUploading" @click="submitOutcome">
                <v-icon left small>{{ getIcon(pendingOutcomeStatus) }}</v-icon>{{ pendingOutcomeButtonLabel }}
              </v-btn>
            </div>
          </div>

          <img v-if="lastPhotoPreview && !pendingOutcomeStatus" class="delivery-photo-preview" :src="lastPhotoPreview" alt="Foto da entrega" />

          <v-textarea
            v-if="!pendingOutcomeStatus"
            class="delivery-comment-field"
            dense
            filled
            :dark="$theme.dark"
            rows="2"
            label="Comentário da entrega"
            v-model="comment"
          />
          <v-btn v-if="!pendingOutcomeStatus" class="delivery-comment-button" block text :dark="$theme.dark" :disabled="!comment" @click="saveComment(currentDelivery, comment)">
            <v-icon left small>mdi-message-text-outline</v-icon>Registrar comentário
          </v-btn>
        </div>

        <div v-if="false" class="playlist">
          <div class="playlist-title">
            <span>Próximas entregas</span>
            <strong>{{ pendingPlaylist.length }}</strong>
          </div>
          <div v-if="loading && pendingPlaylist.length === 0" class="playlist-skeleton">
            <v-skeleton-loader v-for="index in 4" :key="index" type="list-item-avatar-two-line" />
          </div>
          <button
            v-else
            v-for="delivery in pendingPlaylist"
            :key="delivery._id"
            class="playlist-item"
            :class="{ active: currentDelivery && String(currentDelivery._id) === String(delivery._id) }"
            @click="selectDelivery(delivery)"
          >
            <v-icon :color="getColor(delivery.status)">{{ getIcon(delivery.status) }}</v-icon>
            <div>
              <strong>{{ clientName(delivery) }}</strong>
              <span>{{ distanceLabel(delivery) }}</span>
            </div>
            <small>#{{ saleNumber(delivery) }}</small>
          </button>
          <div v-if="!loading && pendingPlaylist.length === 0" class="player-empty">
            <v-icon color="#9aa7a0">mdi-playlist-check</v-icon>
            <span>Nenhuma entrega pendente.</span>
          </div>
          <div v-if="loading && pendingPlaylist.length > 0" class="player-loading">
            <v-progress-circular indeterminate color="primary" />
            <span>Atualizando fila</span>
          </div>
        </div>
      </aside>
    </section>
  </layout>
</template>

<script>
import { getStorage, ref, getDownloadURL, uploadBytesResumable } from "firebase/storage";
import _ from "lodash";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

export default {
  data() {
    return {
      loading: false,
      photoUploading: false,
      actionLoading: "",
      transitionDelivery: null,
      transitionStatus: "",
      deliveriesPayload: { docs: [], summary: {} },
      currentLocation: null,
      mapRouteLocation: null,
      lastHeadingLocation: null,
      playerMapBearing: 0,
      playerDeliveryMap: null,
      playerDeliveryMapLayers: [],
      playerDeliveryMapRenderTimer: null,
      locationStatus: "pending",
      locationWatchId: null,
      selectedId: this.$route.params.id,
      comment: "",
      pendingOutcomeStatus: "",
      pendingOutcomeComment: "",
      pendingOutcomeAttachment: null,
      pendingOutcomeFileName: "",
      pendingOutcomePhotoPreview: "",
      operationalMapCenter: null,
      playerViewportHeight: 0,
      playerBottomGap: 72,
      lastPhotoPreview: "",
      rotas: [
        { name: "Início", path: "/dashboard" },
        { name: "Entregas", path: "/entregas" },
        { name: "Player", path: this.$route.path }
      ],
      options: [
        { icon: "mdi-format-list-bulleted", nome: "Lista", action: () => this.$router.push("/entregas") },
        { icon: "mdi-refresh", nome: "Atualizar", action: () => this.loadData() }
      ]
    };
  },
  computed: {
    deliveries() {
      const docs = this.normalizeDeliveries(this.deliveriesPayload.docs);
      return docs.map(delivery => ({
        ...delivery,
        distance_km: this.distanceFromCurrent(delivery)
      })).sort((a, b) => {
        const distanceA = this.hasDistance(a.distance_km) ? Number(a.distance_km) : Number.MAX_SAFE_INTEGER;
        const distanceB = this.hasDistance(b.distance_km) ? Number(b.distance_km) : Number.MAX_SAFE_INTEGER;
        if (distanceA !== distanceB) return distanceA - distanceB;
        return String(a.visit_date || "").localeCompare(String(b.visit_date || ""));
      });
    },
    pendingPlaylist() {
      return this.deliveries.filter(delivery => ["waiting", "on_the_way"].includes(delivery.status));
    },
    currentDelivery() {
      if (this.transitionDelivery) return null;
      return this.pendingPlaylist.find(delivery => String(delivery._id) === String(this.selectedId)) ||
        this.pendingPlaylist.find(delivery => delivery.status === "on_the_way") ||
        this.pendingPlaylist[0] ||
        null;
    },
    nextPendingDelivery() {
      const finishedId = this.transitionDelivery && this.transitionDelivery._id;
      return this.pendingPlaylist.find(delivery => String(delivery._id) !== String(finishedId)) || null;
    },
    nextDeliveryDisabled() {
      return !this.nextPendingDelivery;
    },
    transitionTitle() {
      return this.transitionStatus === "done" ? "Entrega registrada com sucesso" : "Entrega cancelada";
    },
    transitionMessage() {
      if (this.nextPendingDelivery) return "Tudo certo. A próxima entrega está pronta, mas você decide quando avançar.";
      return "Não há outra entrega pendente na fila agora.";
    },
    routeDurationLabel() {
      const summary = this.currentDelivery && this.currentDelivery.route_summary;
      return summary && summary.duration_text ||
        this.estimatedDurationLabel(this.currentDelivery && this.currentDelivery.distance_km);
    },
    pendingOutcomeTitle() {
      return this.pendingOutcomeStatus === "done" ? "Concluir entrega" : "Registrar tentativa";
    },
    pendingOutcomeButtonLabel() {
      return this.pendingOutcomeStatus === "done" ? "Confirmar conclusão" : "Confirmar tentativa";
    },
    pendingOutcomeColor() {
      return this.pendingOutcomeStatus === "done" ? "green" : "red";
    },
    isSubmittingOutcome() {
      return this.pendingOutcomeStatus && this.actionLoading === this.pendingOutcomeStatus;
    },
    locationLabel() {
      if (this.locationStatus === "ready") return "Tudo certo com sua localização";
      if (this.locationStatus === "denied") return "Localização bloqueada no navegador";
      if (this.locationStatus === "unavailable") return "Aguardando sinal de localização";
      return "Procurando sua posição";
    },
    mapEmbedUrl() {
      const destination = this.deliveryCoordinates(this.currentDelivery);
      const address = this.addressQuery(this.currentDelivery);
      if (!destination && !address) {
        const center = this.mapFallbackCenter();
        return `https://maps.google.com/maps?q=${center.lat},${center.lng}&z=13&output=embed`;
      }
      const destinationValue = destination ? `${destination.lat},${destination.lng}` : address;
      const origin = this.mapRouteLocation || this.currentLocation;
      if (origin) {
        return `https://maps.google.com/maps?saddr=${origin.lat},${origin.lng}&daddr=${destinationValue}&output=embed`;
      }
      return `https://maps.google.com/maps?q=${destinationValue}&z=13&output=embed`;
    },
    mapGoogleUrl() {
      const destination = this.deliveryCoordinates(this.currentDelivery);
      const address = this.addressQuery(this.currentDelivery);
      if (!destination && !address) return "";
      const destinationValue = destination ? `${destination.lat},${destination.lng}` : address;
      if (this.currentLocation) {
        return `https://www.google.com/maps/dir/?api=1&origin=${this.currentLocation.lat},${this.currentLocation.lng}&destination=${destinationValue}&travelmode=driving`;
      }
      return `https://www.google.com/maps/search/?api=1&query=${destinationValue}`;
    },
    playerViewportStyle() {
      const style = {};
      if (this.playerViewportHeight) style["--player-viewport-height"] = `${this.playerViewportHeight}px`;
      if (this.playerBottomGap) style["--player-bottom-gap"] = `${this.playerBottomGap}px`;
      return style;
    }
  },
  watch: {
    "$route.params.id"(id) {
      this.selectedId = id;
      this.resetOutcome();
    },
    currentDelivery(delivery) {
      if (this.transitionDelivery) return;
      if (delivery && String(this.$route.params.id) !== String(delivery._id)) {
        this.$router.replace(`/entregas/player/${delivery._id}`);
      }
      this.schedulePlayerDeliveryMapRender(true);
    },
    currentLocation() {
      this.schedulePlayerDeliveryMapRender();
    }
  },
  methods: {
    loadMapCenter() {
      this.$run("marketplaces/get-map-center", {})
        .then(response => {
          const center = response.data && response.data.center;
          const coords = this.coordinatesFrom(center);
          if (coords) this.operationalMapCenter = coords;
          this.schedulePlayerDeliveryMapRender(true);
        })
        .catch(() => {});
    },
    updatePlayerViewportMetrics() {
      if (typeof window === "undefined") return;
      const viewport = window.visualViewport;
      const height = viewport && viewport.height ? viewport.height : window.innerHeight;
      const hiddenBottom = viewport
        ? Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
        : 0;
      this.playerViewportHeight = Math.round(height || window.innerHeight || 0);
      this.playerBottomGap = Math.max(72, Math.min(96, Math.round(hiddenBottom + 18)));
      this.$nextTick(() => this.schedulePlayerDeliveryMapRender());
    },
    loadData(options = {}) {
      const silent = Boolean(options && options.silent);
      if (!silent) this.loading = true;
      this.$run("external_movimentations/list-my-deliveries", {
        scope: "mine",
        driver_location: this.currentLocation
      }).then(res => {
        this.deliveriesPayload = res.data || { docs: [], summary: {} };
        this.schedulePlayerDeliveryMapRender();
        if (!this.transitionDelivery && this.currentDelivery && this.currentDelivery.status === "waiting") {
          return this.changeStatus(this.currentDelivery, "on_the_way", true);
        }
      }).finally(() => {
        if (!silent) this.loading = false;
      });
    },
    changeStatus(delivery, status, reload = true) {
      if (!delivery || !delivery._id) return Promise.resolve();
      this.actionLoading = status;
      return this.$run("external_movimentations/update-status", {
        _id: delivery._id,
        old_group_name: delivery.status,
        new_group_name: status,
        novo_status: status,
        driver_location: this.currentLocation
      }).then(() => {
        if (status === "done" || status === "canceled") {
          this.comment = "";
          this.lastPhotoPreview = "";
          this.resetOutcome();
          this.transitionDelivery = { ...delivery, status };
          this.transitionStatus = status;
        }
        if (reload) this.loadData();
      }).finally(() => {
        this.actionLoading = "";
      });
    },
    isActionLoading(status) {
      return this.actionLoading === status;
    },
    openOutcome(status) {
      this.pendingOutcomeStatus = status;
      this.pendingOutcomeComment = "";
      this.pendingOutcomeAttachment = null;
      this.pendingOutcomeFileName = "";
      this.pendingOutcomePhotoPreview = "";
    },
    cancelOutcome() {
      this.resetOutcome();
    },
    resetOutcome() {
      this.pendingOutcomeStatus = "";
      this.pendingOutcomeComment = "";
      this.pendingOutcomeAttachment = null;
      this.pendingOutcomeFileName = "";
      this.pendingOutcomePhotoPreview = "";
    },
    registerDeliveryComment(delivery, comment, attachment = null) {
      if (!delivery || !delivery._id || (!comment && !attachment)) return Promise.resolve();
      return this.$run("external_movimentations/add-comment", {
        _id: delivery._id,
        comment: comment || "Foto registrada na entrega.",
        attachment
      });
    },
    saveComment(delivery, comment, attachment = null) {
      return this.registerDeliveryComment(delivery, comment, attachment).then(() => {
        this.comment = "";
        this.loadData();
      });
    },
    submitOutcome() {
      const delivery = this.currentDelivery;
      const status = this.pendingOutcomeStatus;
      if (!delivery || !status || this.photoUploading) return Promise.resolve();

      const comment = this.pendingOutcomeComment ||
        (status === "done" ? "Entrega concluída pelo entregador." : "Entrega não realizada pelo entregador.");

      return this.registerDeliveryComment(delivery, comment, this.pendingOutcomeAttachment)
        .then(() => this.changeStatus(delivery, status));
    },
    uploadPhoto(event) {
      const file = event.target.files && event.target.files[0];
      if (!file || !this.currentDelivery) return;
      this.photoUploading = true;
      const previewUrl = URL.createObjectURL(file);
      if (this.pendingOutcomeStatus) {
        this.pendingOutcomePhotoPreview = previewUrl;
        this.pendingOutcomeFileName = file.name;
      } else {
        this.lastPhotoPreview = previewUrl;
      }
      const storage = getStorage();
      const tenantId = this.$store.getters.getLoggedUser.tenant[0]._id;
      const fileName = `${Date.now()}-${file.name}`;
      const imageRef = ref(storage, `files/${tenantId}/delivery/${this.currentDelivery._id}/${fileName}`);
      const task = uploadBytesResumable(imageRef, file, { contentType: file.type });
      task.on("state_changed", () => {}, () => {
        this.photoUploading = false;
      }, () => {
        getDownloadURL(task.snapshot.ref).then(link => {
          const attachment = {
            type: "photo",
            name: file.name,
            link,
            format: file.name.split(".").pop(),
            size: file.size
          };
          if (this.pendingOutcomeStatus) {
            this.pendingOutcomeAttachment = attachment;
            this.photoUploading = false;
            event.target.value = "";
            return;
          }
          this.saveComment(this.currentDelivery, `Foto registrada: ${file.name}`, attachment)
            .finally(() => {
              this.photoUploading = false;
              event.target.value = "";
            });
        });
      });
    },
    selectDelivery(delivery) {
      this.transitionDelivery = null;
      this.transitionStatus = "";
      this.resetOutcome();
      this.selectedId = delivery._id;
      this.$router.push(`/entregas/player/${delivery._id}`);
      if (delivery.status === "waiting") this.changeStatus(delivery, "on_the_way");
    },
    goToNextDelivery() {
      const next = this.nextPendingDelivery;
      if (!next) return;
      this.selectDelivery(next);
    },
    requestDriverLocation() {
      this.startDriverLocationWatch();
    },
    startDriverLocationWatch() {
      if (!navigator.geolocation) {
        this.locationStatus = "unavailable";
        this.loadData();
        return;
      }
      if (this.locationWatchId !== null) return;
      this.locationStatus = "pending";
      this.locationWatchId = navigator.geolocation.watchPosition(
        position => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            accuracy: position.coords.accuracy,
            updated_at: new Date().toISOString()
          };
          this.updatePlayerHeading(location);
          this.currentLocation = location;
          if (this.shouldRefreshMapRouteLocation(location)) this.mapRouteLocation = location;
          this.locationStatus = "ready";
          this.syncDriverLocation(location);
        },
        error => {
          this.locationStatus = error && error.code === 1 ? "denied" : "unavailable";
          this.loadData();
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 }
      );
    },
    stopDriverLocationWatch() {
      if (this.locationWatchId === null || !navigator.geolocation) return;
      navigator.geolocation.clearWatch(this.locationWatchId);
      this.locationWatchId = null;
    },
    syncDriverLocation: _.throttle(function(location) {
      if (!location) return;
      this.$run("external_movimentations/track-driver-location", {
        driver_location: location
      }).catch(() => {}).finally(() => {
        this.loadData({ silent: true });
      });
    }, 15000, { leading: true, trailing: true }),
    shouldRefreshMapRouteLocation(location) {
      if (!this.mapRouteLocation) return true;
      const movedKm = this.distanceInKm(this.mapRouteLocation, location);
      const updatedAt = this.mapRouteLocation.updated_at ? new Date(this.mapRouteLocation.updated_at).getTime() : 0;
      return movedKm === null || movedKm >= 0.1 || !updatedAt || Date.now() - updatedAt > 120000;
    },
    schedulePlayerDeliveryMapRender(reset = false) {
      clearTimeout(this.playerDeliveryMapRenderTimer);
      this.playerDeliveryMapRenderTimer = setTimeout(() => {
        this.$nextTick(() => this.renderPlayerDeliveryMap(reset));
      }, 80);
    },
    renderPlayerDeliveryMap(reset = false) {
      const container = this.$refs.playerDeliveryMap;
      if (!container) return;

      const center = this.currentLocation || this.deliveryCoordinates(this.currentDelivery) || this.mapFallbackCenter();
      if (!this.playerDeliveryMap || reset) {
        if (this.playerDeliveryMap) this.playerDeliveryMap.remove();
        this.playerDeliveryMap = L.map(container, {
          attributionControl: false,
          center: [center.lat, center.lng],
          dragging: false,
          keyboard: false,
          scrollWheelZoom: false,
          touchZoom: false,
          zoom: 17,
          zoomControl: false
        });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(this.playerDeliveryMap);
      }

      this.playerDeliveryMapLayers.forEach(layer => layer.remove());
      this.playerDeliveryMapLayers = [];

      const routeCoords = this.playerRouteCoordinates(this.currentDelivery);
      const progressCoords = this.playerRouteProgressCoordinates(routeCoords, this.currentLocation);
      if (routeCoords.length > 1) {
        const routeBase = L.polyline(routeCoords, {
          color: "#fbbf24",
          lineCap: "round",
          lineJoin: "round",
          opacity: .96,
          weight: 6
        }).addTo(this.playerDeliveryMap);
        this.playerDeliveryMapLayers.push(routeBase);
      }
      if (progressCoords.length > 1) {
        const routeProgress = L.polyline(progressCoords, {
          color: "#16a34a",
          lineCap: "round",
          lineJoin: "round",
          opacity: .98,
          weight: 7
        }).addTo(this.playerDeliveryMap);
        this.playerDeliveryMapLayers.push(routeProgress);
      }

      const destination = this.deliveryCoordinates(this.currentDelivery);
      if (destination) {
        const destinationMarker = L.marker([destination.lat, destination.lng], {
          icon: this.playerMapMarkerIcon("destination", "#e53935")
        }).addTo(this.playerDeliveryMap);
        this.playerDeliveryMapLayers.push(destinationMarker);
      }

      if (this.currentLocation) {
        const driverMarker = L.marker([this.currentLocation.lat, this.currentLocation.lng], {
          icon: this.playerMapMarkerIcon("driver", "#075e54")
        }).addTo(this.playerDeliveryMap);
        this.playerDeliveryMapLayers.push(driverMarker);
        this.playerDeliveryMap.setView([this.currentLocation.lat, this.currentLocation.lng], 17, { animate: true, duration: .8 });
      } else if (destination) {
        this.playerDeliveryMap.setView([destination.lat, destination.lng], 16, { animate: false });
      }

      this.playerDeliveryMap.invalidateSize();
      this.applyPlayerMapHeading();
    },
    applyPlayerMapHeading() {
      const container = this.$refs.playerDeliveryMap;
      if (!container) return;
      container.style.transform = `rotate(${-this.playerMapBearing}deg)`;
    },
    updatePlayerHeading(location) {
      if (!location) return;
      if (!this.lastHeadingLocation) {
        this.lastHeadingLocation = location;
        return;
      }
      const movedKm = this.distanceInKm(this.lastHeadingLocation, location);
      if (movedKm === null || movedKm < 0.015) return;
      this.playerMapBearing = this.bearingBetween(this.lastHeadingLocation, location);
      this.lastHeadingLocation = location;
    },
    bearingBetween(origin, destination) {
      const from = this.coordinatesFrom(origin);
      const to = this.coordinatesFrom(destination);
      if (!from || !to) return this.playerMapBearing;
      const toRadians = degrees => degrees * Math.PI / 180;
      const toDegrees = radians => radians * 180 / Math.PI;
      const lat1 = toRadians(from.lat);
      const lat2 = toRadians(to.lat);
      const deltaLng = toRadians(to.lng - from.lng);
      const y = Math.sin(deltaLng) * Math.cos(lat2);
      const x = Math.cos(lat1) * Math.sin(lat2) -
        Math.sin(lat1) * Math.cos(lat2) * Math.cos(deltaLng);
      return (toDegrees(Math.atan2(y, x)) + 360) % 360;
    },
    playerRouteCoordinates(delivery = {}) {
      const routeSummary = this.playerRouteSummaryWithPolyline(delivery);
      const decoded = this.decodePolyline(routeSummary.overview_polyline || routeSummary.polyline || routeSummary.encoded_polyline);
      if (decoded.length > 1) return decoded;
      return [];
    },
    playerRouteSummaryWithPolyline(delivery = {}) {
      const summaries = [
        delivery && delivery.route_summary,
        ...(Array.isArray(delivery && delivery.status_history)
          ? [...delivery.status_history].reverse().map(item => item && item.route_summary)
          : []),
      ];
      return summaries.find(summary => summary && (
        summary.overview_polyline ||
        summary.polyline ||
        summary.encoded_polyline
      )) || {};
    },
    playerRouteProgressCoordinates(routeCoords = [], driverLocation = null) {
      if (!routeCoords.length || !driverLocation) return [];
      const driverPoint = [driverLocation.lat, driverLocation.lng];
      let closestIndex = 0;
      let closestDistance = Number.MAX_SAFE_INTEGER;
      routeCoords.forEach((coords, index) => {
        const distance = this.distanceBetweenLatLng(coords, driverPoint);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      return [...routeCoords.slice(0, closestIndex + 1), driverPoint];
    },
    distanceBetweenLatLng(origin, destination) {
      if (!origin || !destination) return Number.MAX_SAFE_INTEGER;
      const fromLat = Number(origin[0]);
      const fromLng = Number(origin[1]);
      const toLat = Number(destination[0]);
      const toLng = Number(destination[1]);
      if (![fromLat, fromLng, toLat, toLng].every(Number.isFinite)) return Number.MAX_SAFE_INTEGER;
      const radius = 6371;
      const toRadians = degrees => degrees * Math.PI / 180;
      const deltaLat = toRadians(toLat - fromLat);
      const deltaLng = toRadians(toLng - fromLng);
      const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(toRadians(fromLat)) * Math.cos(toRadians(toLat)) *
        Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
      return radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    },
    decodePolyline(encoded = "") {
      if (!encoded || typeof encoded !== "string") return [];
      let index = 0;
      let lat = 0;
      let lng = 0;
      const coordinates = [];
      while (index < encoded.length) {
        let shift = 0;
        let result = 0;
        let byte = null;
        do {
          byte = encoded.charCodeAt(index++) - 63;
          result |= (byte & 0x1f) << shift;
          shift += 5;
        } while (byte >= 0x20);
        lat += result & 1 ? ~(result >> 1) : result >> 1;
        shift = 0;
        result = 0;
        do {
          byte = encoded.charCodeAt(index++) - 63;
          result |= (byte & 0x1f) << shift;
          shift += 5;
        } while (byte >= 0x20);
        lng += result & 1 ? ~(result >> 1) : result >> 1;
        coordinates.push([lat / 1e5, lng / 1e5]);
      }
      return coordinates;
    },
    playerMapMarkerIcon(type, color) {
      return L.divIcon({
        className: "player-map-marker",
        html: `
          <div class="player-map-pin" style="--pin-counter-rotation:${this.playerMapBearing}deg;height:62px;position:relative;transform:rotate(${this.playerMapBearing}deg);transform-origin:26px 58px;width:52px;">
            ${this.playerPinSvg(type)}
            <span style="display:block;height:22px;left:15px;position:absolute;top:15px;width:22px;z-index:2;">${this.playerMarkerSvg(type)}</span>
          </div>
        `,
        iconSize: [52, 62],
        iconAnchor: [26, 58],
        popupAnchor: [0, -54]
      });
    },
    playerPinSvg(type) {
      const id = `player-pin-${type}`;
      const isDriver = type === "driver";
      const stops = isDriver
        ? [
          ["0%", "#16a34a"],
          ["34%", "#22c55e"],
          ["58%", "#0ea5e9"],
          ["100%", "#2563eb"],
        ]
        : [
          ["0%", "#ef4444"],
          ["30%", "#f97316"],
          ["55%", "#facc15"],
          ["78%", "#22c55e"],
          ["100%", "#3b82f6"],
        ];
      const gradientStops = stops.map(([offset, color]) => `<stop offset="${offset}" stop-color="${color}" />`).join("");
      return `
        <svg class="player-map-pin-shape" style="display:block;height:62px;width:52px;" viewBox="0 0 64 72" aria-hidden="true">
          <defs>
            <linearGradient id="${id}-paint" x1="10" y1="8" x2="54" y2="64" gradientUnits="userSpaceOnUse">
              ${gradientStops}
            </linearGradient>
            <radialGradient id="${id}-core" cx="30%" cy="22%" r="78%">
              <stop offset="0%" stop-color="#fff7ad" />
              <stop offset="48%" stop-color="#ffd23f" />
              <stop offset="100%" stop-color="#f59e0b" />
            </radialGradient>
            <linearGradient id="${id}-shine" x1="16" y1="10" x2="46" y2="38" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stop-color="#ffffff" stop-opacity=".86" />
              <stop offset="100%" stop-color="#ffffff" stop-opacity="0" />
            </linearGradient>
          </defs>
          <path fill="url(#${id}-paint)" d="M32 4C18.2 4 7 15.2 7 29c0 19.6 25 39 25 39s25-19.4 25-39C57 15.2 45.8 4 32 4Z" />
          <path fill="rgba(255,255,255,.22)" d="M13 29C13 17.6 21.6 9.8 33.5 8.7C25.6 12.2 18.6 21 18 32.2c-.5 9.1 4.2 18.3 10.1 26.4C21.5 52.2 13 40.2 13 29Z" />
          <circle cx="32" cy="29" r="18" fill="rgba(10,20,30,.22)" />
          <circle cx="32" cy="29" r="16" fill="url(#${id}-core)" />
          <path fill="url(#${id}-shine)" d="M21 23c4.2-8.6 16.7-9.5 22.5-2.5c-6.6-2.9-15.5-1.8-22.5 2.5Z" />
        </svg>
      `;
    },
    playerMarkerSvg(type) {
      if (type === "driver") {
        return `
          <svg style="display:block;height:22px;width:22px;" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#ffffff" d="M3 7a2 2 0 0 1 2-2h9v9H3V7Z" />
            <path fill="#ffffff" d="M15 9h3.2c.5 0 1 .24 1.3.64L22 13v1h-7V9Z" />
            <path fill="#ffffff" d="M7 20a2.3 2.3 0 1 0 0-4.6A2.3 2.3 0 0 0 7 20ZM18 20a2.3 2.3 0 1 0 0-4.6A2.3 2.3 0 0 0 18 20Z" />
          </svg>
        `;
      }
      return `
        <svg style="display:block;height:22px;width:22px;" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#ffffff" d="M4 20V9.5L12 4l8 5.5V20h-5v-6H9v6H4Z" />
          <path fill="#ffffff" d="M7 10h10v2H7v-2Z" />
        </svg>
      `;
    },
    normalizeDeliveries(value) {
      if (Array.isArray(value)) return value;
      return [];
    },
    normalizeCoordinate(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    },
    coordinatesFrom(value = {}) {
      if (!value) return null;
      const candidates = [
        value,
        value.location,
        value.coordinates,
        value.geo,
        value.position,
        value.geometry && value.geometry.location,
        value.current_location,
        value.last_location,
        value.address_destiny,
        value.address_destiny && value.address_destiny.location,
        value.address_destiny && value.address_destiny.coordinates,
        value.address_destiny && value.address_destiny.geometry && value.address_destiny.geometry.location,
        value.destination,
        value.delivery_address,
        value.delivery_address && value.delivery_address.location,
        value.delivery_address && value.delivery_address.coordinates,
        value.delivery_address && value.delivery_address.geometry && value.delivery_address.geometry.location,
        value.address,
        value.address && value.address.location,
        value.address && value.address.coordinates,
        value.address && value.address.geometry && value.address.geometry.location,
        value.endereco,
        value.endereco && value.endereco.location,
        value.endereco && value.endereco.coordinates,
        value.endereco && value.endereco.geometry && value.endereco.geometry.location
      ];
      for (const candidate of candidates) {
        const lat = this.normalizeCoordinate(candidate && (candidate.lat || candidate.latitude));
        const lng = this.normalizeCoordinate(candidate && (candidate.lng || candidate.lon || candidate.long || candidate.longitude));
        if (lat !== null && lng !== null) return { lat, lng };
      }
      return null;
    },
    loggedTenant() {
      const logged = this.$store.getters.getLoggedUser || {};
      return logged.activeTenant && typeof logged.activeTenant === "object"
        ? logged.activeTenant
        : Array.isArray(logged.tenant) ? logged.tenant[0] : logged.tenant || {};
    },
    loggedUnity() {
      const logged = this.$store.getters.getLoggedUser || {};
      return logged.activeUnity || logged.active_unity ||
        (Array.isArray(logged.unity) ? logged.unity[0] : logged.unity) ||
        (Array.isArray(logged.unidade) ? logged.unidade[0] : logged.unidade) ||
        null;
    },
    mapFallbackCenter() {
      return this.coordinatesFrom(this.loggedUnity()) ||
        this.operationalMapCenter ||
        this.coordinatesFrom(this.loggedTenant()) ||
        this.currentLocation ||
        { lat: -14.235004, lng: -51.92528 };
    },
    deliveryCoordinates(delivery = {}) {
      delivery = delivery || {};
      const candidates = [
        delivery.address_destiny,
        delivery.destination,
        delivery.delivery_address,
        delivery.address,
        delivery.endereco,
        delivery.sale_summary && delivery.sale_summary.address_destiny,
        delivery.sale_summary && delivery.sale_summary.delivery_address,
        delivery.sale_summary && delivery.sale_summary.address,
        delivery.sale_summary && delivery.sale_summary.compra && delivery.sale_summary.compra.address_destiny,
        delivery.sale_summary && delivery.sale_summary.compra && delivery.sale_summary.compra.delivery_address,
        delivery.sale_summary && delivery.sale_summary.compra && delivery.sale_summary.compra.endereco,
        delivery.client && delivery.client.address,
        delivery.client && delivery.client.endereco
      ];
      for (const candidate of candidates) {
        const coords = this.coordinatesFrom(candidate);
        if (coords) return coords;
      }
      return null;
    },
    addressQuery(delivery = {}) {
      delivery = delivery || {};
      const label = this.addressLabel(delivery.address_destiny || {});
      return label && label !== "Endereço não informado" ? encodeURIComponent(label) : "";
    },
    distanceInKm(origin, destination) {
      const from = this.coordinatesFrom(origin);
      const to = this.coordinatesFrom(destination);
      if (!from || !to) return null;
      const radius = 6371;
      const toRadians = degrees => degrees * Math.PI / 180;
      const deltaLat = toRadians(to.lat - from.lat);
      const deltaLng = toRadians(to.lng - from.lng);
      const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
        Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) *
        Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
      return Math.round(radius * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 10) / 10;
    },
    savedDistanceFrom(delivery = {}) {
      delivery = delivery || {};
      const candidates = [
        delivery.route_summary && delivery.route_summary.distance_km,
        delivery.distance_km,
        delivery.distanceKm,
        delivery.distance,
        delivery.distancia_km,
        delivery.distancia,
        delivery.sale_summary && delivery.sale_summary.distance_km,
        delivery.sale_summary && delivery.sale_summary.distance,
        delivery.sale_summary && delivery.sale_summary.distancia_km,
        delivery.sale_summary && delivery.sale_summary.distancia,
        delivery.sale_summary && delivery.sale_summary.compra && delivery.sale_summary.compra.distance_km,
        delivery.sale_summary && delivery.sale_summary.compra && delivery.sale_summary.compra.distance,
        delivery.sale_summary && delivery.sale_summary.compra && delivery.sale_summary.compra.distancia_km,
        delivery.sale_summary && delivery.sale_summary.compra && delivery.sale_summary.compra.distancia
      ];
      const value = candidates.find(candidate => this.hasDistance(candidate));
      return this.hasDistance(value) ? Number(value) : null;
    },
    routeDistanceText(delivery = {}) {
      delivery = delivery || {};
      return delivery.route_summary && delivery.route_summary.distance_text ||
        delivery.distance_text ||
        delivery.sale_summary && delivery.sale_summary.distance_text ||
        delivery.sale_summary && delivery.sale_summary.compra && delivery.sale_summary.compra.distance_text ||
        "";
    },
    formatDistance(distance) {
      if (!this.hasDistance(distance)) return "Distância indisponível";
      if (distance < 1) return `${Math.round(distance * 1000)} m`;
      return `${Number(distance).toLocaleString("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
    },
    estimatedDurationLabel(distance) {
      if (!this.hasDistance(distance)) return "";
      const minutes = Math.max(1, Math.round((Number(distance) / 35) * 60));
      if (minutes < 60) return `${minutes} min`;
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes ? `${hours} h ${remainingMinutes} min` : `${hours} h`;
    },
    distanceFromCurrent(delivery) {
      const savedDistance = this.savedDistanceFrom(delivery);
      if (this.hasDistance(savedDistance)) return Number(savedDistance);
      if (!this.currentLocation) return null;
      return this.distanceInKm(this.currentLocation, this.deliveryCoordinates(delivery));
    },
    hasDistance(value) {
      return value !== null && value !== undefined && value !== "" && Number.isFinite(Number(value));
    },
    distanceLabel(delivery) {
      const routeText = this.routeDistanceText(delivery);
      if (routeText) return routeText;
      return this.formatDistance(this.distanceFromCurrent(delivery));
    },
    clientName(delivery) {
      return delivery && delivery.client && delivery.client.nome ||
        delivery && delivery.sale_summary && delivery.sale_summary.client && delivery.sale_summary.client.nome ||
        "Cliente não informado";
    },
    saleNumber(delivery) {
      return delivery && delivery.sale_summary && delivery.sale_summary.id_transacao || delivery && delivery.transaction || "-";
    },
    addressLabel(address = {}) {
      address = address || {};
      return [
        address.street,
        address.number ? `nº ${address.number}` : "",
        address.bairro,
        address.city || address.cidade
      ].filter(Boolean).join(", ") || "Endereço não informado";
    },
    getColor(status) {
      return { waiting: "orange", on_the_way: "green", done: "blue", canceled: "red" }[status] || "grey";
    },
    getStatus(status) {
      return { waiting: "Agendada", on_the_way: "Em rota", done: "Concluída", canceled: "Cancelada" }[status] || status;
    },
    getIcon(status) {
      return {
        waiting: "mdi-clock-outline",
        on_the_way: "mdi-truck-fast-outline",
        done: "mdi-check-circle-outline",
        canceled: "mdi-cancel"
      }[status] || "mdi-truck-delivery-outline";
    }
  },
  created() {
    this.loadMapCenter();
    this.startDriverLocationWatch();
  },
  mounted() {
    this.updatePlayerViewportMetrics();
    window.addEventListener("resize", this.updatePlayerViewportMetrics, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.updatePlayerViewportMetrics, { passive: true });
      window.visualViewport.addEventListener("scroll", this.updatePlayerViewportMetrics, { passive: true });
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updatePlayerViewportMetrics);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.updatePlayerViewportMetrics);
      window.visualViewport.removeEventListener("scroll", this.updatePlayerViewportMetrics);
    }
    this.stopDriverLocationWatch();
    if (this.syncDriverLocation && this.syncDriverLocation.cancel) this.syncDriverLocation.cancel();
    clearTimeout(this.playerDeliveryMapRenderTimer);
    this.playerDeliveryMapLayers.forEach(layer => layer.remove());
    this.playerDeliveryMapLayers = [];
    if (this.playerDeliveryMap) {
      this.playerDeliveryMap.remove();
      this.playerDeliveryMap = null;
    }
  }
};
</script>

<style scoped>
.delivery-player {
  background: #f5f7f4;
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr) 380px;
  min-height: calc(100vh - 72px);
  padding: 14px;
}
.delivery-player-dark {
  background: #0b1518;
  color: #edf5f2;
}
.player-map {
  background: #dfeae4;
  border: 1px solid #cbd9d1;
  border-radius: 8px;
  min-height: calc(100vh - 100px);
  overflow: hidden;
  position: relative;
}
.delivery-player-dark .player-map {
  background: #101f24;
  border-color: #244149;
}
.player-leaflet-map {
  bottom: -30%;
  left: -30%;
  position: absolute;
  right: -30%;
  top: -30%;
  transform-origin: 50% 50%;
  transition: transform 1.4s ease;
  z-index: 1;
}
::v-deep .player-map-marker {
  background: transparent;
  border: 0;
}
::v-deep .player-map-pin {
  height: 62px;
  position: relative;
  transform: rotate(var(--pin-counter-rotation));
  transform-origin: 26px 58px;
  width: 52px;
}
::v-deep .player-map-pin-shape {
  filter: drop-shadow(0 10px 16px rgba(7, 80, 89, .28));
  height: 62px;
  width: 52px;
}
::v-deep .player-map-pin span {
  left: 15px;
  position: absolute;
  top: 15px;
  z-index: 2;
}
::v-deep .player-map-pin span svg {
  height: 22px;
  width: 22px;
}
.player-map-empty {
  align-items: center;
  color: #65746b;
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  justify-content: center;
  position: relative;
  text-align: center;
  z-index: 2;
}
.player-map-loading {
  align-items: center;
  background: rgba(9, 24, 29, .72);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 700;
  gap: 8px;
  inset: 0;
  justify-content: center;
  position: absolute;
  z-index: 3;
}
.google-route-button {
  align-items: center;
  background: #1f8f55;
  border-radius: 999px;
  bottom: 16px;
  color: #fff;
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  gap: 6px;
  padding: 10px 14px;
  position: absolute;
  right: 16px;
  text-decoration: none;
  z-index: 3;
}
.player-panel {
  display: grid;
  gap: 12px;
  grid-template-rows: auto;
  min-height: 0;
}
.current-card,
.playlist {
  background: #ffffff;
  border: 1px solid #dde6df;
  border-radius: 8px;
  padding: 16px;
}
.current-card {
  position: relative;
}
.delivery-photo-button {
  background: rgba(23, 40, 32, .08);
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 2;
}
.delivery-player-dark .delivery-photo-button {
  background: rgba(255, 255, 255, .1);
  color: #edf5f2;
}
.delivery-player-dark .current-card,
.delivery-player-dark .playlist {
  background: #101f24;
  border-color: #244149;
}
.current-status,
.player-actions,
.playlist-title,
.playlist-item {
  align-items: center;
  display: flex;
  gap: 8px;
}
.current-status span {
  color: #65746b;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
}
.current-card > strong {
  color: #172820;
  display: block;
  font-size: 24px;
  line-height: 1.15;
  margin-top: 8px;
}
.transition-card {
  align-content: center;
  display: grid;
  gap: 10px;
  min-height: 320px;
  text-align: center;
}
.transition-icon {
  align-items: center;
  background: #78909c;
  border-radius: 50%;
  display: inline-flex;
  height: 72px;
  justify-content: center;
  justify-self: center;
  width: 72px;
}
.transition-icon.done {
  background: #1f8f55;
}
.transition-icon.canceled {
  background: #e53935;
}
.transition-eyebrow {
  color: #1f8f55;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}
.transition-card p {
  color: #65746b;
  font-size: 13px;
  line-height: 1.45;
  margin: 4px 0 10px;
}
.delivery-player-dark .transition-card p {
  color: #b7c8c2;
}
.transition-actions {
  display: grid;
  gap: 8px;
}
.delivery-player-dark .current-card > strong {
  color: #f2fbf7;
}
.current-card small,
.current-card p,
.playlist-item span,
.playlist-item small {
  color: #65746b;
}
.delivery-player-dark .current-card small,
.delivery-player-dark .current-card p,
.delivery-player-dark .playlist-item span,
.delivery-player-dark .playlist-item small,
.delivery-player-dark .current-status span {
  color: #b7c8c2;
}
.current-card p {
  align-items: center;
  display: flex;
  gap: 6px;
  margin: 9px 0 0;
}
.player-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  margin: 14px 0;
}
.outcome-form {
  display: grid;
  gap: 10px;
  margin-top: 12px;
}
.outcome-title,
.outcome-actions,
.outcome-photo-line {
  align-items: center;
  display: flex;
  gap: 8px;
}
.outcome-title strong {
  color: #172820;
  font-size: 15px;
}
.delivery-player-dark .outcome-title strong {
  color: #f2fbf7;
}
.outcome-photo-line {
  background: #f5f7f4;
  border: 1px solid #dde6df;
  border-radius: 8px;
  padding: 8px;
}
.delivery-player-dark .outcome-photo-line {
  background: #0b1518;
  border-color: #244149;
}
.outcome-photo-line img {
  border-radius: 6px;
  height: 42px;
  object-fit: cover;
  width: 42px;
}
.outcome-photo-line span {
  color: #65746b;
  flex: 1;
  font-size: 12px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.outcome-actions {
  justify-content: flex-end;
}
.delivery-photo-preview {
  border-radius: 8px;
  display: block;
  margin: 4px 0 12px;
  max-height: 180px;
  object-fit: cover;
  width: 100%;
}
.playlist {
  min-height: 0;
  overflow: auto;
  position: relative;
}
.playlist-title {
  justify-content: space-between;
  margin-bottom: 10px;
}
.playlist-title span {
  color: #1f8f55;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
}
.playlist-title strong {
  color: #172820;
  font-size: 22px;
}
.delivery-player-dark .playlist-title strong {
  color: #f2fbf7;
}
.playlist-item {
  background: #f5f7f4;
  border: 1px solid #e1e9e3;
  border-radius: 8px;
  color: #172820;
  margin-bottom: 8px;
  padding: 12px;
  text-align: left;
  width: 100%;
}
.playlist-item.active {
  border-color: #1f8f55;
  box-shadow: inset 3px 0 0 #1f8f55;
}
.delivery-player-dark .playlist-item {
  background: #0b1518;
  border-color: #244149;
  color: #f2fbf7;
}
.playlist-item div {
  flex: 1;
  min-width: 0;
}
.playlist-item strong,
.playlist-item span {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.player-empty,
.player-loading {
  align-items: center;
  color: #65746b;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-height: 160px;
  text-align: center;
}
.player-loading {
  min-height: 72px;
}
.playlist-skeleton {
  display: grid;
  gap: 8px;
}
@media(max-width: 980px) {
  .delivery-player {
    grid-template-columns: 1fr;
    padding: 10px;
  }
  .player-map {
    min-height: 48vh;
  }
}
@media(max-width: 640px) {
  .delivery-player {
    display: block;
    gap: 0;
    height: var(--player-viewport-height, 100dvh);
    inset: 0;
    min-height: var(--player-viewport-height, 100dvh);
    overflow: hidden;
    padding: 0;
    position: fixed;
    z-index: 2000;
  }
  .player-map {
    border: 0;
    border-radius: 0;
    height: 100%;
    min-height: 100%;
    width: 100%;
  }
  .player-panel {
    bottom: calc(var(--player-bottom-gap, 72px) + env(safe-area-inset-bottom));
    display: block;
    left: 12px;
    position: absolute;
    right: 12px;
    z-index: 5;
  }
  .current-card {
    border-radius: 18px;
    box-shadow: 0 18px 50px rgba(7, 20, 16, .26);
    max-height: calc(var(--player-viewport-height, 100dvh) - var(--player-bottom-gap, 72px) - 18px - env(safe-area-inset-bottom));
    overflow: auto;
    overscroll-behavior: contain;
    padding: 14px 12px max(12px, env(safe-area-inset-bottom));
    scrollbar-width: none;
  }
  .current-card::-webkit-scrollbar {
    display: none;
  }
  .current-card > strong {
    font-size: 18px;
    padding-right: 44px;
  }
  .current-card p {
    font-size: 12px;
    margin-top: 7px;
  }
  .player-actions {
    background: linear-gradient(180deg, rgba(255, 255, 255, .78), #ffffff 28%);
    display: grid;
    gap: 8px;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    margin: 10px -2px 0;
    padding-top: 8px;
    position: sticky;
    bottom: 0;
    z-index: 3;
  }
  .delivery-player-dark .player-actions {
    background: linear-gradient(180deg, rgba(16, 31, 36, .78), #101f24 28%);
  }
  .player-actions .v-btn {
    height: auto !important;
    min-height: 44px;
    min-width: 0 !important;
    margin: 0;
    padding: 0 8px !important;
  }
  .player-actions .v-btn >>> .v-btn__content {
    font-size: 11px;
    line-height: 1.15;
    white-space: normal;
    width: 100%;
  }
  .player-actions .v-btn >>> .v-icon {
    margin-right: 3px !important;
  }
  .outcome-actions {
    background: linear-gradient(180deg, rgba(255, 255, 255, .78), #ffffff 28%);
    display: grid;
    gap: 8px;
    grid-template-columns: minmax(0, .7fr) minmax(0, 1.3fr);
    margin: 0 -2px;
    padding-top: 8px;
    position: sticky;
    bottom: 0;
    z-index: 3;
  }
  .delivery-player-dark .outcome-actions {
    background: linear-gradient(180deg, rgba(16, 31, 36, .78), #101f24 28%);
  }
  .outcome-actions .v-btn {
    min-width: 0 !important;
    padding: 0 8px !important;
  }
  .outcome-actions .v-btn >>> .v-btn__content {
    font-size: 11px;
    white-space: normal;
  }
  .delivery-comment-field,
  .delivery-comment-button,
  .delivery-photo-preview {
    display: none;
  }
  .google-route-button {
    display: none;
  }
  .transition-card {
    min-height: auto;
  }
  .transition-actions {
    grid-template-columns: 1fr;
  }
}
</style>
